import {useMemo, useState} from 'react'
import {useMutation, useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {CardSection} from '@/components/shared/CardSection'
import {DetailField} from '@/components/shared/DetailField'
import {TextLink} from '@/components/shared/TextLink'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {Button} from '@/components/ui/button'
import {ErrorBanner} from '@/components/ui/error-banner'
import type {ComboboxOption} from '@/components/ui/combobox'
import type {
    CertificationCampaign,
    CertificationPolicy,
    CreateCampaignRequest,
} from '@/types/certification'
import {ReviewerType} from '@/types/certification'
import {ScopeMode} from '@/features/certification/scope-mode'
import {useCertificationScopePreview} from '@/features/certification/hooks/useCertificationScopePreview'
import type {WizardFormState} from '@/features/certification/hooks/useCertificationPolicyWizardForm'
import {CampaignFormFields, isCampaignFormValid} from '@/features/certification/components/CampaignFormFields'

export interface WizardStepLaunchProps {
    form: WizardFormState
    policy: CertificationPolicy
    isEditMode: boolean
    onBack: () => void
}

/**
 * LAUNCH step — terminal actions after the policy is persisted. Renders the
 * success banner, a read-only summary of Basics / Scope / Review Settings, the
 * final scope count with the frequency-filter disclaimer (spec §3.4), and the
 * Finish / Stage / Stage & activate actions. Stage paths render the shared
 * {@link CampaignFormFields}. Handles the partial-failure, open-campaign-warning,
 * and mixed-permission states (spec §3.5, §7.3, §7.4).
 */
export function WizardStepLaunch({form, policy, isEditMode, onBack}: WizardStepLaunchProps) {
    const i18n = useI18n()
    const navigate = useNavigate()
    const services = useServices()
    const {toast} = useToast()
    const {hasPermission} = useAuth()
    const canCreateCampaign = hasPermission(permissions.certifications.create)

    const [campaignForm, setCampaignForm] = useState<CreateCampaignRequest>({
        name: String(i18n.certificationPolicies.wizard.launch.campaignNamePrefill({policyName: policy.name})),
        description: null,
        dueDate: '',
        ownerPartyId: policy.parameter?.owner?.id ?? '',
    })
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('')
    const [stagedCampaignId, setStagedCampaignId] = useState<string | null>(null)
    const [activationFailed, setActivationFailed] = useState(false)

    const {data: selectedOwner} = useQuery({
        queryKey: ['identities', campaignForm.ownerPartyId],
        queryFn: () => services.identities.detail(campaignForm.ownerPartyId),
        enabled: !!campaignForm.ownerPartyId,
    })

    const {data: ownerSearchData, isLoading: ownerSearchLoading} = useQuery({
        queryKey: ['identities', 'search', ownerSearchQuery],
        queryFn: () => services.identities.list({q: ownerSearchQuery, size: 10}),
    })
    const ownerOptions: ComboboxOption[] = (ownerSearchData?.content ?? []).map((identity) => ({
        value: identity.id,
        label: identity.party.displayName || identity.identityType,
        description: identity.identityType,
    }))

    const {data: campaignSummary} = useQuery({
        queryKey: ['certification-policy', policy.id, 'campaign-summary'],
        queryFn: () => services.certificationPolicies.campaignSummary(policy.id),
    })

    const preview = useCertificationScopePreview({
        scopeMode: form.scopeMode,
        conditions: form.conditions,
        conditionExpr: form.conditionExpr,
    })

    const stageMutation = useMutation({
        mutationFn: (request: CreateCampaignRequest) => services.certificationCampaigns.create(request),
    })
    const activateMutation = useMutation({
        mutationFn: (id: string) => services.certification.activateCampaign(id),
    })
    const isStaging = stageMutation.isPending || activateMutation.isPending

    const buildStageRequest = (): CreateCampaignRequest => ({
        ...campaignForm,
        certificationPolicyId: policy.id,
    })

    const onStage = () => {
        setActivationFailed(false)
        stageMutation.mutate(buildStageRequest(), {
            onSuccess: (campaign: CertificationCampaign) => {
                toast({description: String(i18n.certificationPolicies.wizard.launch.stagedToast)})
                navigate(routes.accessGovernance.certificationCampaigns.detail(campaign.id))
            },
        })
    }

    const onStageAndActivate = () => {
        setActivationFailed(false)
        stageMutation.mutate(buildStageRequest(), {
            onSuccess: (campaign: CertificationCampaign) => {
                setStagedCampaignId(campaign.id)
                activateMutation.mutate(campaign.id, {
                    onSuccess: () => {
                        toast({description: String(i18n.certificationPolicies.wizard.launch.stagedAndActivatedToast)})
                        navigate(routes.accessGovernance.certificationCampaigns.detail(campaign.id))
                    },
                    onError: () => setActivationFailed(true),
                })
            },
        })
    }

    const onFinish = () => {
        toast({description: String(i18n.certificationPolicies.wizard.launch.finishedToast)})
        navigate(routes.accessGovernance.certificationPolicies.detail(policy.id))
    }

    const reviewerTypeLabel = useMemo(() => {
        switch (form.reviewerType) {
            case ReviewerType.MANAGER:
                return i18n.certificationPolicies.wizard.reviewerTypeManagerDesc
            case ReviewerType.PROFILE_OWNER:
                return i18n.certificationPolicies.wizard.reviewerTypeProfileOwnerDesc
            case ReviewerType.RESOURCE_OWNER:
                return i18n.certificationPolicies.wizard.reviewerTypeResourceOwnerDesc
            case ReviewerType.CAMPAIGN_OWNER:
                return i18n.certificationPolicies.wizard.reviewerTypeCampaignOwnerDesc
            default:
                return form.reviewerType
        }
    }, [form.reviewerType, i18n])

    const scopeSummary = form.scopeMode === ScopeMode.EXPRESSION
        ? form.conditionExpr || i18n.certificationPolicies.wizard.noConditionsInfo
        : (form.conditions.length === 0
            ? i18n.certificationPolicies.wizard.noConditionsInfo
            : i18n.certificationPolicies.wizard.previewInScope({
                matched: preview.data?.matched ?? 0,
                total: preview.data?.totalEvaluated ?? 0,
            }))

    const stageDisabled = isStaging || !isCampaignFormValid(campaignForm)

    return (
        <Stack gap="md">
            <InfoBanner variant="success" data-testid="wizard-policy-saved-banner">
                {isEditMode
                    ? i18n.certificationPolicies.wizard.policyUpdated
                    : i18n.certificationPolicies.wizard.policySaved}
            </InfoBanner>

            <MutedText block data-testid="wizard-launch-already-saved-note">
                {i18n.certificationPolicies.wizard.launch.alreadySavedNote}
            </MutedText>

            <CardSection
                title={i18n.certificationPolicies.wizard.launch.summaryTitle}
                data-testid="launch-summary"
            >
                <Stack gap="sm">
                    <DetailField
                        label={String(i18n.certificationPolicies.wizard.launch.summaryBasics)}
                        value={policy.name}
                    />
                    <DetailField
                        label={String(i18n.certificationPolicies.wizard.launch.summaryScope)}
                        value={scopeSummary}
                    />
                    <DetailField
                        label={String(i18n.certificationPolicies.wizard.launch.summaryReviewSettings)}
                        value={reviewerTypeLabel}
                    />
                    <DetailField
                        label={String(i18n.certificationPolicies.wizard.launch.scopeCountLabel)}
                        value={
                            <span data-testid="launch-scope-count">
                                {i18n.certificationPolicies.wizard.launch.scopeCountValue({
                                    matched: preview.data?.matched ?? 0,
                                    total: preview.data?.totalEvaluated ?? 0,
                                })}
                            </span>
                        }
                    />
                    <MutedText size="xs" block data-testid="launch-scope-disclaimer">
                        {i18n.certificationPolicies.wizard.previewFrequencyDisclaimer}
                    </MutedText>
                </Stack>
            </CardSection>

            {campaignSummary?.hasOpenCampaign && (
                <InfoBanner variant="warning" data-testid="launch-open-campaign-warning">
                    {i18n.certificationPolicies.wizard.launch.openCampaignWarning}
                </InfoBanner>
            )}

            <CardSection
                title={i18n.certificationPolicies.wizard.launch.sectionTitle}
                data-testid="launch-section"
            >
                <Stack gap="md">
                    {canCreateCampaign ? (
                        <>
                            <CampaignFormFields
                                value={campaignForm}
                                onChange={setCampaignForm}
                                ownerOptions={ownerOptions}
                                onOwnerSearch={setOwnerSearchQuery}
                                ownerSearchLoading={ownerSearchLoading}
                                selectedOwnerLabel={selectedOwner?.party?.displayName || selectedOwner?.identityType}
                                disabled={isStaging}
                            />

                            {stageMutation.error && !activationFailed && (
                                <div data-testid="launch-stage-error">
                                    <ErrorBanner error={stageMutation.error as Error}/>
                                </div>
                            )}

                            {activationFailed && stagedCampaignId && (
                                <Stack gap="sm" data-testid="launch-partial-failure">
                                    <InfoBanner variant="destructive">
                                        {i18n.certificationPolicies.wizard.launch.partialFailure}
                                    </InfoBanner>
                                    <InlineRow>
                                        <TextLink
                                            to={routes.accessGovernance.certificationCampaigns.detail(stagedCampaignId)}
                                            testId="launch-go-to-campaign"
                                        >
                                            {i18n.certificationPolicies.wizard.launch.goToCampaign}
                                        </TextLink>
                                    </InlineRow>
                                </Stack>
                            )}

                            <InlineRow>
                                <Button
                                    variant="outline"
                                    onClick={onBack}
                                    disabled={isStaging}
                                    data-testid="btn-launch-back"
                                >
                                    {i18n.certificationPolicies.wizard.back}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={onFinish}
                                    disabled={isStaging}
                                    data-testid="btn-finish"
                                >
                                    {i18n.certificationPolicies.wizard.finish}
                                </Button>
                                <Button
                                    onClick={onStage}
                                    disabled={stageDisabled}
                                    data-testid="btn-launch-stage"
                                >
                                    {isStaging ? <ButtonSpinner/> : null}
                                    {i18n.certificationPolicies.wizard.launch.stage}
                                </Button>
                                <Button
                                    onClick={onStageAndActivate}
                                    disabled={stageDisabled}
                                    data-testid="btn-launch-stage-activate"
                                >
                                    {isStaging ? <ButtonSpinner/> : null}
                                    {i18n.certificationPolicies.wizard.launch.stageAndActivate}
                                </Button>
                            </InlineRow>
                        </>
                    ) : (
                        <>
                            <MutedText block data-testid="launch-no-campaign-permission">
                                {i18n.certificationPolicies.wizard.launch.noCampaignPermission}
                            </MutedText>
                            <InlineRow>
                                <Button
                                    variant="outline"
                                    onClick={onBack}
                                    data-testid="btn-launch-back"
                                >
                                    {i18n.certificationPolicies.wizard.back}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={onFinish}
                                    data-testid="btn-finish"
                                >
                                    {i18n.certificationPolicies.wizard.finish}
                                </Button>
                            </InlineRow>
                        </>
                    )}
                </Stack>
            </CardSection>
        </Stack>
    )
}
